import { memo, useCallback, useState } from "react";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";

import { AutomationsPanelSection } from "../../automation";
import { getDefaultTemplateCategoryForAutomation } from "../../automation/constants/automationUiConstants";
import type { SystemDefaultAutomationId } from "../../automation/constants/automationUiConstants";
import { ClinicalCrmCommunicationsSection } from "../../communications";
import type { FluxmedDefaultTemplateCategory } from "../../messageTemplates/utils/messageTemplateUiUtils";
import FluxZapQuickActionsPanel from "./FluxZapQuickActionsPanel";
import { CrmStatisticsSection } from "../../statistics";
import type {
  ClinicalCrmSectionId,
  ClinicalCrmSectionsBoardModel,
} from "../types";
import ClinicalCrmSortableSectionCard from "./ClinicalCrmSortableSectionCard";
import ClinicalCrmTemplatesAndQuestionnairesTabsSection from "./ClinicalCrmTemplatesAndQuestionnairesTabsSection";
import SectionHeader from "src-next/shared/ui/sections/SectionHeader";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import WhatsAppEndpointContactsSection from "./WhatsAppEndpointContactsPanel";
import { useAutomationChannelImpact } from "../hooks/useAutomationChannelImpact";

export interface ClinicalCrmSectionsBoardProps {
  model: ClinicalCrmSectionsBoardModel;
  /**
   * Oculta a seção de Automações (ex.: organização ecommerce).
   * Somente camada de renderização — preferências e backend intactos.
   */
  hideAutomationsSection?: boolean;
}

const ClinicalCrmSectionsBoard = memo(
  ({ model, hideAutomationsSection = false }: ClinicalCrmSectionsBoardProps) => {
    const { layout, content } = model;

    /** Signal para refresh de estatísticas após envio de mensagem */
    const [franchiseStatsRefreshSignal, setFranchiseStatsRefreshSignal] =
      useState(0);
    const [defaultTemplatesCategoryFocus, setDefaultTemplatesCategoryFocus] =
      useState<FluxmedDefaultTemplateCategory | null>(null);
    const [automationRefreshSignal, setAutomationRefreshSignal] = useState(0);

    const handleMessageSent = useCallback(() => {
      setFranchiseStatsRefreshSignal((prev) => prev + 1);
    }, []);

    /** Expande a seção (se recolhida) e rola até o bloco no board. */
    const handleFocusSection = useCallback(
      (sectionId: ClinicalCrmSectionId) => {
        if (layout.collapsedMap[sectionId]) {
          layout.handleToggleSectionCollapsed(sectionId);
        }
        requestAnimationFrame(() => {
          document
            .getElementById(`crm-section-${sectionId}`)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      },
      [layout.collapsedMap, layout.handleToggleSectionCollapsed],
    );

    const handleNavigateToDefaultTemplates = useCallback(
      (automationId: SystemDefaultAutomationId) => {
        content.setActiveTabId("defaultTemplates");
        setDefaultTemplatesCategoryFocus(
          getDefaultTemplateCategoryForAutomation(automationId),
        );
        handleFocusSection("content");
      },
      [content.setActiveTabId, handleFocusSection],
    );

    const handleDefaultTemplatesCategoryFocusConsumed = useCallback(() => {
      setDefaultTemplatesCategoryFocus(null);
    }, []);

    const {
      hasConfiguredAutomations,
      removeAllAutomationConfigs,
      refreshAutomationConfigStatus,
    } = useAutomationChannelImpact({
      organizationId: content.organizationId,
      practitionerId: content.practitionerId,
      wabaId: content.wabaId,
    });

    const handleAfterWhatsappChannelChange = useCallback(async () => {
      await refreshAutomationConfigStatus();
      setAutomationRefreshSignal((previous) => previous + 1);
    }, [refreshAutomationConfigStatus]);

    /**
     * Seções "automations" e "statistics" ainda não entram na UI; exibimos
     * templates, comunicações e WhatsApp (pós-consulta só via acesso rápido em dialog).
     *
     * Importante:
     * - Mantemos `layout.sectionOrder` intacto para não quebrar preferências salvas.
     * - Filtramos somente na camada de renderização e alinhamos `SortableContext.items`
     *   com o que é efetivamente exibido para evitar warnings do DnD.
     */
    const visibleSectionOrder = layout.sectionOrder.filter((sectionId) => {
      if (hideAutomationsSection && sectionId === "automations") return false;
      return ["automations", "content", "communications", "whatsapp"].includes(
        sectionId,
      );
    });

    return (
      <Stack spacing={2.5}>
        <FluxZapQuickActionsPanel
          context="crm"
          cardSize="compact"
          organizationId={content.organizationId}
          practitionerId={content.practitionerId}
          onMessageSent={handleMessageSent}
          onFocusSection={handleFocusSection}
          statsRefreshSignal={franchiseStatsRefreshSignal}
        />

        <Divider sx={{ borderColor: "divider", opacity: 0.8 }} />

        <DndContext
          sensors={layout.sensors}
          collisionDetection={layout.collisionDetection}
          onDragEnd={layout.handleDragEnd}
        >
          <SortableContext
            items={visibleSectionOrder}
            strategy={layout.sortingStrategy}
          >
            <Stack spacing={0}>
              {visibleSectionOrder.map((sectionId, sectionIndex) => {
                const isLastSection =
                  sectionIndex === visibleSectionOrder.length - 1;

                return (
                  <Box key={sectionId} id={`crm-section-${sectionId}`}>
                    <ClinicalCrmSortableSectionCard id={sectionId}>
                      {({ dragHandleProps }) => {
                        if (sectionId === "automations") {
                          return (
                            <AutomationsPanelSection
                              context={{
                                organizationId: content.organizationId,
                                practitionerId: content.practitionerId,
                                whatsappBusinessAccountId: content.wabaId,
                              }}
                              hasActiveMetaEndpoint={
                                content.hasActiveMetaEndpoint
                              }
                              isEndpointStatusLoading={
                                content.isEndpointStatusLoading
                              }
                              onNavigateToDefaultTemplates={
                                handleNavigateToDefaultTemplates
                              }
                              isCollapsed={layout.collapsedMap[sectionId]}
                              onToggleCollapsed={() =>
                                layout.handleToggleSectionCollapsed(sectionId)
                              }
                              dragHandleProps={dragHandleProps}
                              hasConfiguredCustomAutomations={
                                hasConfiguredAutomations
                              }
                              automationRefreshSignal={automationRefreshSignal}
                            />
                          );
                        }

                        if (sectionId === "content") {
                          return (
                            <ClinicalCrmTemplatesAndQuestionnairesTabsSection
                              organizationId={content.organizationId}
                              practitionerId={content.practitionerId}
                              activeTabId={content.activeTabId}
                              onChangeTab={content.setActiveTabId}
                              messageTemplates={content.messageTemplates}
                              isCollapsed={layout.collapsedMap[sectionId]}
                              onToggleCollapsed={() =>
                                layout.handleToggleSectionCollapsed(sectionId)
                              }
                              dragHandleProps={dragHandleProps}
                              onCreateTemplate={content.handleCreateTemplate}
                              onUpdateTemplate={content.handleUpdateTemplate}
                              onDeleteTemplate={content.handleDeleteTemplate}
                              onRefreshMessageTemplates={
                                content.handleRefreshMessageTemplates
                              }
                              isMessageTemplatesLoading={
                                content.isMessageTemplatesLoading
                              }
                              onMessageSent={handleMessageSent}
                              franchiseRefreshSignal={
                                franchiseStatsRefreshSignal
                              }
                              hasActiveMetaEndpoint={
                                content.hasActiveMetaEndpoint
                              }
                              isEndpointStatusLoading={
                                content.isEndpointStatusLoading
                              }
                              whatsappChannelOption={
                                content.whatsappChannelOption
                              }
                              defaultTemplatesCategoryFocus={
                                defaultTemplatesCategoryFocus
                              }
                              onDefaultTemplatesCategoryFocusConsumed={
                                handleDefaultTemplatesCategoryFocusConsumed
                              }
                            />
                          );
                        }

                        if (sectionId === "communications") {
                          return (
                            <ClinicalCrmCommunicationsSection
                              organizationId={content.organizationId}
                              practitionerId={content.practitionerId}
                              statsRefreshSignal={franchiseStatsRefreshSignal}
                              onMessageSent={handleMessageSent}
                              hasActiveMetaEndpoint={
                                content.hasActiveMetaEndpoint
                              }
                              isCollapsed={layout.collapsedMap[sectionId]}
                              onToggleCollapsed={() =>
                                layout.handleToggleSectionCollapsed(sectionId)
                              }
                              dragHandleProps={dragHandleProps}
                            />
                          );
                        }

                        if (sectionId === "whatsapp") {
                          const wabaId = content.wabaId;

                          return (
                            <Box>
                              <SectionHeader
                                icon={<WhatsAppIcon fontSize="small" />}
                                title="Configurações WhatsApp"
                                subtitle="Número preferencial, plano Fluxmed ou assinatura Meta conforme o canal configurado."
                                isCollapsed={layout.collapsedMap[sectionId]}
                                onCollapsedChange={() =>
                                  layout.handleToggleSectionCollapsed(sectionId)
                                }
                                actions={
                                  <IconButton
                                    size="small"
                                    aria-label="Arrastar para reordenar a seção na página"
                                    sx={{ cursor: "grab" }}
                                    {...(dragHandleProps?.attributes ?? {})}
                                    {...(dragHandleProps?.listeners ?? {})}
                                  >
                                    <DragIndicatorIcon fontSize="small" />
                                  </IconButton>
                                }
                              />
                              {!layout.collapsedMap[sectionId] ? (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    p: { xs: 1.25, sm: 1.5 },
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "action.hover",
                                  }}
                                >
                                  <WhatsAppEndpointContactsSection
                                    organizationId={content.organizationId}
                                    wabaId={wabaId}
                                    whatsappChannelOption={
                                      content.whatsappChannelOption
                                    }
                                    setupConfiguration={
                                      content.crmSetupConfiguration
                                    }
                                    hasFluxZapExtensionActive={
                                      content.hasFluxZapExtensionActive
                                    }
                                    isFluxZapExtensionLoading={
                                      content.isFluxZapExtensionLoading
                                    }
                                    onFluxZapExtensionActivated={
                                      content.refetchFluxZapExtensionStatus
                                    }
                                    franchiseStatsRefreshSignal={
                                      franchiseStatsRefreshSignal
                                    }
                                    onFluxmedCheckoutCompleted={
                                      handleMessageSent
                                    }
                                    hasConfiguredAutomations={
                                      hasConfiguredAutomations
                                    }
                                    removeAllAutomationConfigs={
                                      removeAllAutomationConfigs
                                    }
                                    persistedWabaId={wabaId}
                                    onAfterChannelChange={
                                      handleAfterWhatsappChannelChange
                                    }
                                  />
                                </Box>
                              ) : null}
                            </Box>
                          );
                        }

                        return (
                          <CrmStatisticsSection
                            isCollapsed={layout.collapsedMap[sectionId]}
                            onToggleCollapsed={() =>
                              layout.handleToggleSectionCollapsed(sectionId)
                            }
                            dragHandleProps={dragHandleProps}
                          />
                        );
                      }}
                    </ClinicalCrmSortableSectionCard>

                    {!isLastSection && (
                      <Divider
                        sx={{ my: 2.5, borderColor: "divider", opacity: 0.8 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Stack>
          </SortableContext>
        </DndContext>
      </Stack>
    );
  },
);

ClinicalCrmSectionsBoard.displayName = "ClinicalCrmSectionsBoard";

export default ClinicalCrmSectionsBoard;
